import { map } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { KeywordModalState } from "../../api/Blueprints";
import {
  useCreateKeywordGroup,
  useUpdateKeywordGroup,
} from "../../api/BlueprintsApi";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../../shared-components/Button";
import ErrorMessageContainer from "../../shared-components/ErrorMessageContainer";
import InputField from "../../shared-components/InputField";
import LoadingSpinner from "../../shared-components/LoadingSpinner";
import { Modal } from "../../shared-components/Modal";
import { classNames } from "../../utils";
import { checkStateForDuplicates } from "./helpers";
import KeywordListSection from "./KeywordListSection";

type Props = {
  showModal: boolean;
  close: () => void;
  initialState?: KeywordModalState;
  blueprintUUID?: string;
  behaviorKitIdUUID?: string;
  onSave: () => void;
  keywordUUID?: string;
};

function checkStateForMax(state: KeywordModalState, maxListLength: number) {
  return map(
    [
      state.includeExact,
      state.includeFuzzy,
      state.excludeExact,
      state.excludeFuzzy,
    ],
    (p) => p.length > maxListLength
  ).reduce((a, b) => a || b);
}

export const KeywordGroupButtonAdd = ({
  blueprintUUID,
  behaviorKitUUID,
  onSave,
  className,
}: {
  blueprintUUID?: string;
  behaviorKitUUID?: string;
  onSave: () => void;
  className?: string;
}) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <div className={className}>
      <Button
        text="Add New Keyword Group"
        colorType={ButtonColorTypes.custom}
        type={ButtonTypes.add}
        className="border p-0 "
        onClick={(e) => {
          setShowModal(true);
          e.preventDefault();
        }}
      />
      <KeywordModal
        showModal={showModal}
        close={() => setShowModal(false)}
        blueprintUUID={blueprintUUID}
        behaviorKitIdUUID={behaviorKitUUID}
        onSave={onSave}
      />
    </div>
  );
};
const emptyState = {
  includeExact: [],
  includeFuzzy: [],
  excludeExact: [],
  excludeFuzzy: [],
  name: "",
};
const maxListLength = 1000;
export const KeywordModal = ({
  showModal,
  close,
  initialState,
  blueprintUUID,
  behaviorKitIdUUID,
  onSave,
  keywordUUID,
}: Props) => {
  const [state, setState] = useState<KeywordModalState>(
    initialState ?? { ...emptyState }
  );
  const isEdit = !!initialState;
  const {
    mutate: createKeywordGroup,
    isLoading,
    isError,
    isSuccess,
    reset,
  } = useCreateKeywordGroup();
  const {
    mutate: UpdateKeywordGroup,
    isLoading: isLoadingUpdate,
    isError: isErrorUpdate,
    isSuccess: isSuccessUpdate,
    reset: resetUpdate,
  } = useUpdateKeywordGroup();

  const [error, setError] = useState("");
  const [softError, setSoftError] = useState("");

  const handleClose = useCallback(() => {
    setState(initialState ?? { ...emptyState });
    setSoftError("");
    close();
  }, [initialState, close]);

  useEffect(() => {
    const hasDuplicates = checkStateForDuplicates(state);

    const hasListWithMoreThanMax = checkStateForMax(state, maxListLength);
    let errorMessage = "";

    if (hasDuplicates) {
      errorMessage =
        "Duplicate word appears in multiple list, please fix before saving";
    }

    if (hasListWithMoreThanMax) {
      errorMessage = `One or more lists has more than ${maxListLength} words`;
    }
    setError(errorMessage);
  }, [state]);

  useEffect(() => {
    if (isSuccess || isSuccessUpdate) {
      onSave();
      handleClose();
    }
    if (isError || isSuccess) {
      reset();
    }
    if (isErrorUpdate || isSuccessUpdate) {
      resetUpdate();
    }
  }, [
    isSuccessUpdate,
    isErrorUpdate,
    isSuccess,
    onSave,
    handleClose,
    reset,
    resetUpdate,
    isError,
  ]);

  function handleSave() {
    if (!state.name) {
      setError(`Keyword Group Name is required.`);
    } else {
      if (isEdit) {
        UpdateKeywordGroup({
          external_id: keywordUUID ?? "",
          exclude_exact: state.excludeExact,
          exclude_fuzzy: state.excludeFuzzy,
          include_exact: state.includeExact,
          include_fuzzy: state.includeFuzzy,
          name: state.name,
        });
      } else {
        createKeywordGroup({
          blueprint_id: blueprintUUID,
          behavior_kit_id: behaviorKitIdUUID,
          exclude_exact: state.excludeExact,
          exclude_fuzzy: state.excludeFuzzy,
          include_exact: state.includeExact,
          include_fuzzy: state.includeFuzzy,
          name: state.name,
        });
      }
    }
  }

  function updateList(
    keywordType:
      | "includeExact"
      | "includeFuzzy"
      | "excludeExact"
      | "excludeFuzzy",
    list: string[]
  ) {
    state[keywordType] = [...list];

    setState({ ...state });
  }

  function updateName(name: string) {
    setState({ ...state, name });
  }

  if (isError) setError("Error Saving Keyword Group please try again.");

  return (
    <Modal open={showModal} onClose={close} width={"w-full max-w-4xl p-3"}>
      <div>
        {isEdit ? (
          <div className="text-2xl font-bold border-b-2 mb-3 pb-3">
            Edit Keyword Group
          </div>
        ) : (
          <div className="text-2xl font-bold border-b-2 mb-3 pb-3">
            Add New Keyword Group
          </div>
        )}
        <div className="pb-3">
          <h1 className="text-lg font-bold pb-3">Keyword Group Name</h1>
          <InputField
            handleChange={(e) => {
              updateName(e.target.value);
            }}
            name={`keyword-name-input`}
            placeholder="Type here ..."
            classes="w-full"
            className={classNames("mb-0 w-full")}
            value={state.name}
          />
        </div>
        <div className="pb-3">
          <h1 className="text-lg font-bold">Included</h1>
          <div className="flex gap-4">
            <KeywordListSection
              title="exact"
              tooltip="Search for exact matches. You can also enter a list of comma-separated keywords. Example: Smoke will match `I smoke.` but not 'I smoked'."
              initialList={state.includeExact}
              onChange={(list) => updateList("includeExact", list)}
              onError={setSoftError}
              stateKey="includeExact"
              state={state}
            />
            <KeywordListSection
              title="Fuzzy"
              tooltip="Search for 'fuzzy' matches with word-stems. You can also enter a list of comma-separated keywords. Example: 'smok' will match on 'smoke', 'smoking', 'smoked', etc.."
              initialList={state.includeFuzzy}
              onChange={(list) => updateList("includeFuzzy", list)}
              onError={setSoftError}
              stateKey="includeFuzzy"
              state={state}
            />
          </div>
        </div>
        <div>
          <h2 className="text-lg font-bold">Excluded</h2>
          <div className="flex gap-4">
            <KeywordListSection
              title="exact"
              tooltip="Search for exact matches. You can also enter a list of comma-separated keywords. Example: Smoke will match `I smoke.` but not 'I smoked'."
              initialList={state.excludeExact}
              onChange={(list) => updateList("excludeExact", list)}
              onError={setSoftError}
              chipType="exclude"
              stateKey="excludeExact"
              state={state}
            />
            <KeywordListSection
              title="fuzzy"
              tooltip="Search for 'fuzzy' matches with word-stems. You can also enter a list of comma-separated keywords. Example: 'smok' will match on 'smoke', 'smoking', 'smoked', etc.."
              initialList={state.excludeFuzzy}
              onChange={(list) => updateList("excludeFuzzy", list)}
              onError={setSoftError}
              chipType="exclude"
              stateKey="excludeFuzzy"
              state={state}
            />
          </div>
        </div>
        {error && (
          <div className="py-2">
            <ErrorMessageContainer message={error} />
          </div>
        )}
        {softError && (
          <div className="py-2">
            <ErrorMessageContainer message={softError} />
          </div>
        )}
        <div className="flex justify-end gap-4 pt-6">
          {!isLoading && !isLoadingUpdate && (
            <Button
              text="Cancel"
              colorType={ButtonColorTypes.cancel}
              onClick={handleClose}
            />
          )}
          {(isLoading || isLoadingUpdate) && <LoadingSpinner />}
          {!error && !isLoading && !isErrorUpdate && !isLoadingUpdate && (
            <Button
              text={"Save"}
              onClick={() => {
                handleSave();
              }}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};
